import { Chip, type ChipRootProps } from "@heroui/react";
import {
  type AppChipColor,
  type AppChipVariant,
  type AppChipSize,
} from "./variants";

/**
 * Constrained chip props.
 * className escape hatch available for one-off overrides.
 */
export type AppChipProps = Omit<
  ChipRootProps,
  "className" | "color" | "variant" | "size"
> & {
  /** Semantic color. @default "default" */
  color?: AppChipColor;
  /** Visual variant. @default "secondary" */
  variant?: AppChipVariant;
  /** Size. @default "md" */
  size?: AppChipSize;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppChip — Design system chip / badge.
 *
 * Wraps HeroUI v3 Chip with constrained Rosé Pine semantic colors.
 * Sub-components (Label) are HeroUI pass-throughs.
 *
 * @example
 * ```tsx
 * <AppChip color="success" variant="soft" size="sm">Active</AppChip>
 * ```
 */
function AppChipRoot({
  color = "default",
  variant = "secondary",
  size = "md",
  className,
  ...props
}: AppChipProps) {
  return <Chip {...props} color={color} variant={variant} size={size} className={className} />;
}

export const AppChip = Object.assign(AppChipRoot, {
  Label: Chip.Label,
});
